'use client';

import { Mail, MapPin, Clock, ArrowRight, Loader2, Check, CheckCircle2, AlertTriangle, X as XIcon } from 'lucide-react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { useScrollReveal } from '@/app/hooks/use-scroll-reveal';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

const CONTACT_INFO = [
  { icon: Mail, label: 'Email us', value: 'hello@sandroninfotec.ie' },
  { icon: MapPin, label: 'Based in', value: 'Dublin, Ireland' },
  { icon: Clock, label: 'Office hours', value: 'Mon\u2013Fri \u00b7 9:00\u201318:00 IST' },
] as const;

const contactSchema = z.object({
  name: z.string().min(2, 'Please tell us your name'),
  email: z.string().email('Please use a valid email'),
  topic: z.enum(['general', 'spacenest', 'partnership', 'careers']),
  message: z.string().min(10, 'Add a few more words so we can help'),
  consent: z.boolean().refine((v) => v, 'You must agree to be contacted'),
});

type ContactFormData = z.infer<typeof contactSchema>;

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

interface ToastData {
  variant: 'success' | 'error';
  title: string;
  body: string;
}

function Toast({ data, onDismiss }: { data: ToastData; onDismiss: () => void }) {
  useEffect(() => {
    const timeout = data.variant === 'success' ? 5000 : 8000;
    const timer = setTimeout(onDismiss, timeout);
    return () => clearTimeout(timer);
  }, [data.variant, onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }}
      role={data.variant === 'success' ? 'status' : 'alert'}
      className="fixed right-4 top-6 z-[100] w-[calc(100%-32px)] max-w-[360px] rounded-[14px] border border-sandron-ink-100 bg-white p-4 shadow-[0_16px_40px_-16px_rgba(11,18,32,0.18)] md:right-6"
    >
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          data.variant === 'success' ? 'bg-[rgba(31,107,68,0.12)]' : 'bg-[#FEE2E2]'
        }`}>
          {data.variant === 'success' ? (
            <CheckCircle2 size={20} className="text-spacenest-primary" />
          ) : (
            <AlertTriangle size={20} className="text-[#DC2626]" />
          )}
        </div>
        <div className="flex-1">
          <p className="font-display text-sm font-semibold text-sandron-ink-900">{data.title}</p>
          <p className="mt-0.5 text-[13px] text-sandron-ink-700">{data.body}</p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-sandron-ink-50"
          aria-label="Dismiss"
        >
          <XIcon size={14} />
        </button>
      </div>
    </motion.div>
  );
}

function ContactForm() {
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [toast, setToast] = useState<ToastData | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { topic: 'general', consent: false },
  });

  const onSubmit = async (_data: ContactFormData) => {
    setSubmitStatus('loading');
    // TODO PR #5: replace with real fetch to /api/contact
    await new Promise((resolve) => setTimeout(resolve, 1200));
    setSubmitStatus('success');
    setToast({
      variant: 'success',
      title: 'Message sent',
      body: "Thanks \u2014 we'll reply within two working days.",
    });
    setTimeout(() => {
      setSubmitStatus('idle');
      reset();
    }, 3000);
  };

  const dismissToast = useCallback(() => setToast(null), []);

  const fieldClasses = (hasError: boolean) =>
    `w-full rounded-xl border-[1.5px] px-4 py-3 text-[15px] text-sandron-ink-900 placeholder:text-sandron-ink-500 outline-none transition-colors duration-200 ${
      hasError
        ? 'border-[#DC2626] bg-[#FEF2F2]'
        : 'border-sandron-ink-100 bg-white hover:border-sandron-ink-300 focus:border-spacenest-primary focus:ring-2 focus:ring-spacenest-primary/20'
    }`;

  return (
    <>
      <AnimatePresence>
        {toast && <Toast data={toast} onDismiss={dismissToast} />}
      </AnimatePresence>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="rounded-3xl border border-sandron-ink-100 bg-white p-7 shadow-[0_8px_32px_-12px_rgba(11,18,32,0.08)] md:p-10"
        noValidate
      >
        <div className="flex flex-col gap-5">
          {/* Name */}
          <div>
            <label htmlFor="name" className="mb-1.5 block text-[13px] font-medium text-sandron-ink-700">
              Name <span className="text-spacenest-primary">*</span>
            </label>
            <input
              id="name"
              type="text"
              placeholder="Your name"
              aria-required="true"
              aria-invalid={!!errors.name}
              disabled={submitStatus === 'loading'}
              className={fieldClasses(!!errors.name)}
              {...register('name')}
            />
            {errors.name && (
              <p className="mt-1.5 text-[13px] text-[#DC2626]" aria-live="polite">{errors.name.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="mb-1.5 block text-[13px] font-medium text-sandron-ink-700">
              Email <span className="text-spacenest-primary">*</span>
            </label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              aria-required="true"
              aria-invalid={!!errors.email}
              disabled={submitStatus === 'loading'}
              className={fieldClasses(!!errors.email)}
              {...register('email')}
            />
            {errors.email && (
              <p className="mt-1.5 text-[13px] text-[#DC2626]" aria-live="polite">{errors.email.message}</p>
            )}
          </div>

          {/* Topic */}
          <div>
            <label htmlFor="topic" className="mb-1.5 block text-[13px] font-medium text-sandron-ink-700">
              Topic
            </label>
            <select
              id="topic"
              disabled={submitStatus === 'loading'}
              className={fieldClasses(!!errors.topic)}
              {...register('topic')}
            >
              <option value="general">General enquiry</option>
              <option value="spacenest">About SpaceNest</option>
              <option value="partnership">Partnership</option>
              <option value="careers">Careers</option>
            </select>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="mb-1.5 block text-[13px] font-medium text-sandron-ink-700">
              Message <span className="text-spacenest-primary">*</span>
            </label>
            <textarea
              id="message"
              rows={4}
              placeholder="Tell us about your project or question..."
              aria-required="true"
              aria-invalid={!!errors.message}
              disabled={submitStatus === 'loading'}
              className={`${fieldClasses(!!errors.message)} resize-y`}
              {...register('message')}
            />
            {errors.message && (
              <p className="mt-1.5 text-[13px] text-[#DC2626]" aria-live="polite">{errors.message.message}</p>
            )}
          </div>

          {/* Consent */}
          <div>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                aria-required="true"
                aria-invalid={!!errors.consent}
                disabled={submitStatus === 'loading'}
                className="mt-0.5 h-5 w-5 rounded-md border-[1.5px] border-sandron-ink-300 accent-spacenest-primary"
                {...register('consent')}
              />
              <span className="text-sm leading-[1.5] text-sandron-ink-700">
                I agree to be contacted about my enquiry. See our{' '}
                <Link href="/legal/privacy" className="text-sandron-ink-900 hover:underline">
                  privacy policy
                </Link>.
              </span>
            </label>
            {errors.consent && (
              <p className="mt-1.5 text-[13px] text-[#DC2626]" aria-live="polite">{errors.consent.message}</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitStatus === 'loading' || submitStatus === 'success'}
            aria-busy={submitStatus === 'loading'}
            className={`mt-2 inline-flex h-14 min-w-[200px] items-center justify-center gap-2 rounded-full px-7 font-display text-base font-semibold transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-spacenest-primary disabled:cursor-not-allowed ${
              submitStatus === 'success'
                ? 'bg-spacenest-primary text-white'
                : submitStatus === 'error'
                ? 'border-[1.5px] border-[#DC2626] bg-sandron-ink-900 text-white'
                : 'bg-sandron-ink-900 text-white hover:-translate-y-px hover:shadow-[0_12px_32px_-12px_rgba(11,18,32,0.45)]'
            }`}
          >
            {submitStatus === 'loading' && (
              <>Sending\u2026 <Loader2 size={16} className="animate-spin" /></>
            )}
            {submitStatus === 'success' && (
              <>Sent \u2014 we&apos;ll be in touch <Check size={16} /></>
            )}
            {submitStatus === 'error' && <>Try again</>}
            {submitStatus === 'idle' && (
              <>Send message <ArrowRight size={16} /></>
            )}
          </button>
        </div>
      </form>
    </>
  );
}

export function ContactSection() {
  const { ref, isVisible } = useScrollReveal();
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = (delay: number, y = 16) => ({
    initial: { opacity: 0, y: prefersReducedMotion ? 0 : y },
    animate: isVisible ? { opacity: 1, y: 0 } : { opacity: 0 },
    transition: {
      duration: prefersReducedMotion ? 0.15 : 0.6,
      delay: delay / 1000,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  });

  return (
    <section id="contact" className="bg-white py-[clamp(96px,12vw,160px)]">
      <div
        ref={ref}
        className="mx-auto grid max-w-[1200px] items-start gap-12 px-5 md:px-10 lg:grid-cols-[1fr_1.2fr] lg:gap-20 lg:px-0"
      >
        {/* Left — heading + contact info */}
        <div>
          <motion.div {...fadeUp(0, 12)} className="flex items-center gap-3">
            <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
              Get in touch
            </span>
          </motion.div>

          <motion.h2
            {...fadeUp(90, 20)}
            className="mt-4 max-w-[480px] font-display text-[clamp(36px,4.5vw,64px)] font-bold leading-[1.06] tracking-[-0.025em] text-sandron-ink-900 [text-wrap:balance]"
          >
            Let&apos;s talk.
          </motion.h2>

          <motion.p
            {...fadeUp(180, 16)}
            className="mt-5 max-w-[440px] text-[17px] leading-[1.65] text-sandron-ink-700"
          >
            Whether you run a community centre, want to partner on a product, or
            are curious about working together — we&apos;d love to hear from you.
          </motion.p>

          {/* Contact info list */}
          <div className="mt-10 flex flex-col gap-6">
            {CONTACT_INFO.map((item, i) => (
              <motion.div
                key={item.label}
                {...fadeUp(240 + i * 60, 12)}
                className="group flex items-center gap-4"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sandron-ink-50 text-sandron-ink-900 transition-colors duration-200 group-hover:bg-[rgba(31,107,68,0.12)] group-hover:text-spacenest-primary">
                  <item.icon size={20} strokeWidth={1.75} />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.06em] text-sandron-ink-500">
                    {item.label}
                  </p>
                  <p className="text-base font-medium text-sandron-ink-900">
                    {item.value}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right ��� form */}
        <motion.div
          initial={{ opacity: 0, x: prefersReducedMotion ? 0 : 32 }}
          animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0 }}
          transition={{
            duration: prefersReducedMotion ? 0.15 : 0.7,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
          }}
        >
          <ContactForm />
        </motion.div>
      </div>
    </section>
  );
}
